import { type FocusEvent, useCallback, useEffect } from 'react';

import { TextInput, type TextInputProps } from 'src/components/base';
import { withField } from 'src/helpers';
import { type IWithFieldErrorProps, withFieldError } from 'src/helpers/withFieldError';
import type { IFieldProps } from 'src/types';

type TextInputFieldProps = IFieldProps &
  Omit<TextInputProps, 'onChange'> &
  Omit<IWithFieldErrorProps, 'isShowErrorMessage'>;

export const TextInputField = withField(
  withFieldError((props: TextInputFieldProps) => {
    const {
      field,
      form: { setFieldValue, setFieldTouched },
      touchOnMount,

      onBlur,

      ...rest
    } = props;

    const onChange = useCallback(
      async (value: string | number | null) => {
        await setFieldValue(field.name, value);
        await setFieldTouched(field.name, true);
      },
      [setFieldValue, field.name, setFieldTouched]
    );

    const handleBlur = useCallback(
      async (event: FocusEvent<HTMLInputElement, Element>) => {
        await setFieldTouched(field.name, true);

        onBlur?.(event);
      },
      [setFieldTouched, field.name, onBlur]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: false
    useEffect(() => {
      if (touchOnMount) {
        setTimeout(() => void setFieldTouched(field.name, true));
      }
    }, []);

    return <TextInput {...field} {...rest} onChange={onChange} onBlur={handleBlur} />;
  })
);
